import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Fragment as Fragment$1, useMemo, useCallback, useRef, useState, useEffect } from "react";
import { format } from "date-fns";
import md5 from "md5";
import { createState } from "@tecnobit-srl/state";
import { useToggle } from "@tecnobit-srl/hook-use-toggle";
function isObject(value) {
  return typeof value === "object" && value != null && !Array.isArray(value);
}
var isObjectOrArray = (obj) => typeof obj === "object" && obj !== null;
function compact(value) {
  return Object.fromEntries(Object.entries(value ?? {}).filter(([_, value2]) => value2 !== void 0));
}
var isBaseCondition = (v) => v === "base";
function filterBaseConditions(c) {
  return c.slice().filter((v) => !isBaseCondition(v));
}
function toChar(code) {
  return String.fromCharCode(code + (code > 25 ? 39 : 97));
}
function toName(code) {
  let name = "";
  let x;
  for (x = Math.abs(code); x > 52; x = x / 52 | 0)
    name = toChar(x % 52) + name;
  return toChar(x % 52) + name;
}
function toPhash(h, x) {
  let i = x.length;
  while (i)
    h = h * 33 ^ x.charCodeAt(--i);
  return h;
}
function toHash(value) {
  return toName(toPhash(5381, value) >>> 0);
}
var importantRegex = /\s*!(important)?/i;
function isImportant(value) {
  return typeof value === "string" ? importantRegex.test(value) : false;
}
function withoutImportant(value) {
  return typeof value === "string" ? value.replace(importantRegex, "").trim() : value;
}
function withoutSpace(str) {
  return typeof str === "string" ? str.replaceAll(" ", "_") : str;
}
var memo = (fn) => {
  const cache = /* @__PURE__ */ new Map();
  const get = (...args) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
  return get;
};
var MERGE_OMIT = /* @__PURE__ */ new Set(["__proto__", "constructor", "prototype"]);
function mergeProps(...sources) {
  return sources.reduce((prev, obj) => {
    if (!obj)
      return prev;
    Object.keys(obj).forEach((key) => {
      if (MERGE_OMIT.has(key))
        return;
      const prevValue = prev[key];
      const value = obj[key];
      if (isObject(prevValue) && isObject(value)) {
        prev[key] = mergeProps(prevValue, value);
      } else {
        prev[key] = value;
      }
    });
    return prev;
  }, {});
}
var isNotNullish = (element) => element != null;
function walkObject(target, predicate, options = {}) {
  const { stop, getKey } = options;
  function inner(value, path = []) {
    if (isObjectOrArray(value)) {
      const result = {};
      for (const [prop, child] of Object.entries(value)) {
        const key = (getKey == null ? void 0 : getKey(prop, child)) ?? prop;
        const childPath = [...path, key];
        if (stop == null ? void 0 : stop(value, childPath)) {
          return predicate(value, path);
        }
        const next = inner(child, childPath);
        if (isNotNullish(next)) {
          result[key] = next;
        }
      }
      return result;
    }
    return predicate(value, path);
  }
  return inner(target);
}
function toResponsiveObject(values, breakpoints) {
  return values.reduce(
    (acc, current, index) => {
      const key = breakpoints[index];
      if (current != null) {
        acc[key] = current;
      }
      return acc;
    },
    {}
  );
}
function normalizeStyleObject(styles, context2, shorthand = true) {
  const { utility, conditions: conditions2 } = context2;
  const { hasShorthand, resolveShorthand: resolveShorthand2 } = utility;
  return walkObject(
    styles,
    (value) => {
      return Array.isArray(value) ? toResponsiveObject(value, conditions2.breakpoints.keys) : value;
    },
    {
      stop: (value) => Array.isArray(value),
      getKey: shorthand ? (prop) => hasShorthand ? resolveShorthand2(prop) : prop : void 0
    }
  );
}
var fallbackCondition = {
  shift: (v) => v,
  finalize: (v) => v,
  breakpoints: { keys: [] }
};
var sanitize = (value) => typeof value === "string" ? value.replaceAll(/[\n\s]+/g, " ") : value;
function createCss(context2) {
  const { utility, hash, conditions: conds = fallbackCondition } = context2;
  const formatClassName = (str) => [utility.prefix, str].filter(Boolean).join("-");
  const hashFn = (conditions2, className) => {
    let result;
    if (hash) {
      const baseArray = [...conds.finalize(conditions2), className];
      result = formatClassName(utility.toHash(baseArray, toHash));
    } else {
      const baseArray = [...conds.finalize(conditions2), formatClassName(className)];
      result = baseArray.join(":");
    }
    return result;
  };
  return memo(({ base, ...styles } = {}) => {
    const styleObject = Object.assign(styles, base);
    const normalizedObject = normalizeStyleObject(styleObject, context2);
    const classNames = /* @__PURE__ */ new Set();
    walkObject(normalizedObject, (value, paths) => {
      if (value == null)
        return;
      const important = isImportant(value);
      const [prop, ...allConditions] = conds.shift(paths);
      const conditions2 = filterBaseConditions(allConditions);
      const transformed = utility.transform(prop, withoutImportant(sanitize(value)));
      let className = hashFn(conditions2, transformed.className);
      if (important)
        className = `${className}!`;
      classNames.add(className);
    });
    return Array.from(classNames).join(" ");
  });
}
function compactStyles(...styles) {
  return styles.flat().filter((style) => isObject(style) && Object.keys(compact(style)).length > 0);
}
function createMergeCss(context2) {
  function resolve(styles) {
    const allStyles = compactStyles(...styles);
    if (allStyles.length === 1)
      return allStyles;
    return allStyles.map((style) => normalizeStyleObject(style, context2));
  }
  function mergeCss2(...styles) {
    return mergeProps(...resolve(styles));
  }
  function assignCss2(...styles) {
    return Object.assign({}, ...resolve(styles));
  }
  return { mergeCss: memo(mergeCss2), assignCss: assignCss2 };
}
var wordRegex = /([A-Z])/g;
var msRegex = /^ms-/;
var hypenateProperty = memo((property) => {
  if (property.startsWith("--"))
    return property;
  return property.replace(wordRegex, "-$1").replace(msRegex, "-ms-").toLowerCase();
});
var lengthUnits = "cm,mm,Q,in,pc,pt,px,em,ex,ch,rem,lh,rlh,vw,vh,vmin,vmax,vb,vi,svw,svh,lvw,lvh,dvw,dvh,cqw,cqh,cqi,cqb,cqmin,cqmax,%";
`(?:${lengthUnits.split(",").join("|")})`;
const conditionsStr = "_hover,_focus,_focusWithin,_focusVisible,_disabled,_active,_visited,_target,_readOnly,_readWrite,_empty,_checked,_enabled,_expanded,_highlighted,_complete,_incomplete,_dragging,_before,_after,_firstLetter,_firstLine,_marker,_selection,_file,_backdrop,_first,_last,_only,_even,_odd,_firstOfType,_lastOfType,_onlyOfType,_peerFocus,_peerHover,_peerActive,_peerFocusWithin,_peerFocusVisible,_peerDisabled,_peerChecked,_peerInvalid,_peerExpanded,_peerPlaceholderShown,_groupFocus,_groupHover,_groupActive,_groupFocusWithin,_groupFocusVisible,_groupDisabled,_groupChecked,_groupExpanded,_groupInvalid,_indeterminate,_required,_valid,_invalid,_autofill,_inRange,_outOfRange,_placeholder,_placeholderShown,_pressed,_selected,_grabbed,_underValue,_overValue,_atValue,_default,_optional,_open,_closed,_fullscreen,_loading,_hidden,_current,_currentPage,_currentStep,_today,_unavailable,_rangeStart,_rangeEnd,_now,_topmost,_motionReduce,_motionSafe,_print,_landscape,_portrait,_dark,_light,_osDark,_osLight,_highContrast,_lessContrast,_moreContrast,_ltr,_rtl,_scrollbar,_scrollbarThumb,_scrollbarTrack,_horizontal,_vertical,_icon,_starting,sm,smOnly,smDown,md,mdOnly,mdDown,tablet,tabletOnly,tabletDown,lg,lgOnly,lgDown,xl,xlOnly,xlDown,desktop,desktopOnly,desktopDown,2xl,2xlOnly,2xlDown,smToMd,smToTablet,smToLg,smToXl,smToDesktop,smTo2xl,mdToTablet,mdToLg,mdToXl,mdToDesktop,mdTo2xl,tabletToLg,tabletToXl,tabletToDesktop,tabletTo2xl,lgToXl,lgToDesktop,lgTo2xl,xlToDesktop,xlTo2xl,desktopTo2xl,@/xs,@/sm,@/md,@/lg,@/xl,@/2xl,@/3xl,@/4xl,@/5xl,@/6xl,@/7xl,@/8xl,base";
const conditions = new Set(conditionsStr.split(","));
const conditionRegex = /^@|&|&$/;
function isCondition(value) {
  return conditions.has(value) || conditionRegex.test(value);
}
const underscoreRegex = /^_/;
const conditionsSelectorRegex = /&|@/;
function finalizeConditions(paths) {
  return paths.map((path) => {
    if (conditions.has(path)) {
      return path.replace(underscoreRegex, "");
    }
    if (conditionsSelectorRegex.test(path)) {
      return `[${withoutSpace(path.trim())}]`;
    }
    return path;
  });
}
function sortConditions(paths) {
  return paths.sort((a, b) => {
    const aa = isCondition(a);
    const bb = isCondition(b);
    if (aa && !bb) return 1;
    if (!aa && bb) return -1;
    return 0;
  });
}
const utilities = "aspectRatio:asp,boxDecorationBreak:bx-db,zIndex:z,boxSizing:bx-s,objectPosition:obj-p,objectFit:obj-f,overscrollBehavior:ovs-b,overscrollBehaviorX:ovs-bx,overscrollBehaviorY:ovs-by,position:pos/1,top:top,left:left,inset:inset,insetInline:inset-x/insetX,insetBlock:inset-y/insetY,insetBlockEnd:inset-be,insetBlockStart:inset-bs,insetInlineEnd:inset-e/insetEnd/end,insetInlineStart:inset-s/insetStart/start,right:right,bottom:bottom,float:float,visibility:vis,display:d,hideFrom:hide,hideBelow:show,flexBasis:flex-b,flex:flex,flexDirection:flex-d/flexDir,flexGrow:flex-g,flexShrink:flex-sh,gridTemplateColumns:grid-tc,gridTemplateRows:grid-tr,gridColumn:grid-c,gridRow:grid-r,gridColumnStart:grid-cs,gridColumnEnd:grid-ce,gridAutoFlow:grid-af,gridAutoColumns:grid-ac,gridAutoRows:grid-ar,gap:gap,gridGap:grid-g,gridRowGap:grid-rg,gridColumnGap:grid-cg,rowGap:rg,columnGap:cg,justifyContent:jc,alignContent:ac,alignItems:ai,alignSelf:as,padding:p/1,paddingLeft:pl/1,paddingRight:pr/1,paddingTop:pt/1,paddingBottom:pb/1,paddingBlock:py/1/paddingY,paddingBlockEnd:pbe,paddingBlockStart:pbs,paddingInline:px/paddingX/1,paddingInlineEnd:pe/1/paddingEnd,paddingInlineStart:ps/1/paddingStart,marginLeft:ml/1,marginRight:mr/1,marginTop:mt/1,marginBottom:mb/1,margin:m/1,marginBlock:my/1/marginY,marginBlockEnd:mbe,marginBlockStart:mbs,marginInline:mx/1/marginX,marginInlineEnd:me/1/marginEnd,marginInlineStart:ms/1/marginStart,spaceX:sx,spaceY:sy,outlineWidth:ring-w/ringWidth,outlineColor:ring-c/ringColor,outline:ring/1,outlineOffset:ring-o/ringOffset,divideX:dvd-x,divideY:dvd-y,divideColor:dvd-c,divideStyle:dvd-s,width:w/1,inlineSize:w-is,minWidth:min-w/minW,minInlineSize:min-w-is,maxWidth:max-w/maxW,maxInlineSize:max-w-is,height:h/1,blockSize:h-bs,minHeight:min-h/minH,minBlockSize:min-h-bs,maxHeight:max-h/maxH,maxBlockSize:max-b,color:c,fontFamily:ff,fontSize:fs,fontSizeAdjust:fs-a,fontPalette:fp,fontKerning:fk,fontFeatureSettings:ff-s,fontWeight:fw,fontSmoothing:fsmt,fontVariant:fv,fontVariantAlternates:fv-alt,fontVariantCaps:fv-caps,fontVariationSettings:fv-s,fontVariantNumeric:fv-num,letterSpacing:ls,lineHeight:lh,textAlign:ta,textDecoration:td,textDecorationColor:td-c,textEmphasisColor:te-c,textDecorationStyle:td-s,textDecorationThickness:td-t,textUnderlineOffset:tu-o,textTransform:tt,textIndent:ti,textShadow:tsh,textShadowColor:tsh-c/textShadowColor,textOverflow:tov,verticalAlign:va,wordBreak:wb,textWrap:tw,truncate:trunc,lineClamp:lc,listStyleType:li-t,listStylePosition:li-pos,listStyleImage:li-img,listStyle:li-s,backgroundPosition:bg-p/bgPosition,backgroundPositionX:bg-p-x/bgPositionX,backgroundPositionY:bg-p-y/bgPositionY,backgroundAttachment:bg-a/bgAttachment,backgroundClip:bg-cp/bgClip,background:bg/1,backgroundColor:bg-c/bgColor,backgroundOrigin:bg-o/bgOrigin,backgroundImage:bg-i/bgImage,backgroundRepeat:bg-r/bgRepeat,backgroundBlendMode:bg-bm/bgBlendMode,backgroundSize:bg-s/bgSize,backgroundGradient:bg-grad/bgGradient,textGradient:txt-grad,gradientFromPosition:grad-from-pos,gradientToPosition:grad-to-pos,gradientFrom:grad-from,gradientTo:grad-to,gradientVia:grad-via,gradientViaPosition:grad-via-pos,borderRadius:bdr/rounded,borderTopLeftRadius:bdr-tl/roundedTopLeft,borderTopRightRadius:bdr-tr/roundedTopRight,borderBottomRightRadius:bdr-br/roundedBottomRight,borderBottomLeftRadius:bdr-bl/roundedBottomLeft,borderTopRadius:bdr-t/roundedTop,borderRightRadius:bdr-r/roundedRight,borderBottomRadius:bdr-b/roundedBottom,borderLeftRadius:bdr-l/roundedLeft,borderStartStartRadius:bdr-ss/roundedStartStart,borderStartEndRadius:bdr-se/roundedStartEnd,borderStartRadius:bdr-s/roundedStart,borderEndStartRadius:bdr-es/roundedEndStart,borderEndEndRadius:bdr-ee/roundedEndEnd,borderEndRadius:bdr-e/roundedEnd,border:bd,borderWidth:bd-w,borderTopWidth:bd-t-w,borderLeftWidth:bd-l-w,borderRightWidth:bd-r-w,borderBottomWidth:bd-b-w,borderBlockStartWidth:bd-bs-w,borderBlockEndWidth:bd-be-w,borderColor:bd-c,borderInline:bd-x/borderX,borderInlineWidth:bd-x-w/borderXWidth,borderInlineColor:bd-x-c/borderXColor,borderBlock:bd-y/borderY,borderBlockWidth:bd-y-w/borderYWidth,borderBlockColor:bd-y-c/borderYColor,borderLeft:bd-l,borderLeftColor:bd-l-c,borderInlineStart:bd-s/borderStart,borderInlineStartWidth:bd-s-w/borderStartWidth,borderInlineStartColor:bd-s-c/borderStartColor,borderRight:bd-r,borderRightColor:bd-r-c,borderInlineEnd:bd-e/borderEnd,borderInlineEndWidth:bd-e-w/borderEndWidth,borderInlineEndColor:bd-e-c/borderEndColor,borderTop:bd-t,borderTopColor:bd-t-c,borderBottom:bd-b,borderBottomColor:bd-b-c,borderBlockEnd:bd-be,borderBlockEndColor:bd-be-c,borderBlockStart:bd-bs,borderBlockStartColor:bd-bs-c,opacity:op,boxShadow:bx-sh/shadow,boxShadowColor:bx-sh-c/shadowColor,mixBlendMode:mix-bm,filter:filter,brightness:brightness,contrast:contrast,grayscale:grayscale,hueRotate:hue-rotate,invert:invert,saturate:saturate,sepia:sepia,dropShadow:drop-shadow,blur:blur,backdropFilter:bkdp,backdropBlur:bkdp-blur,backdropBrightness:bkdp-brightness,backdropContrast:bkdp-contrast,backdropGrayscale:bkdp-grayscale,backdropHueRotate:bkdp-hue-rotate,backdropInvert:bkdp-invert,backdropOpacity:bkdp-opacity,backdropSaturate:bkdp-saturate,backdropSepia:bkdp-sepia,borderCollapse:bd-cl,borderSpacing:bd-sp,borderSpacingX:bd-sx,borderSpacingY:bd-sy,tableLayout:tbl,transitionTimingFunction:trs-tmf,transitionDelay:trs-dly,transitionDuration:trs-dur,transitionProperty:trs-prop,transition:trs,animation:anim,animationName:anim-n,animationTimingFunction:anim-tmf,animationDuration:anim-dur,animationDelay:anim-dly,animationPlayState:anim-ps,animationComposition:anim-comp,animationFillMode:anim-fm,animationDirection:anim-dir,animationIterationCount:anim-ic,animationRange:anim-r,animationState:anim-s,animationRangeStart:anim-rs,animationRangeEnd:anim-re,animationTimeline:anim-tl,transformOrigin:trf-o,transformBox:trf-b,transformStyle:trf-s,transform:trf,rotate:rotate,rotateX:rotate-x,rotateY:rotate-y,rotateZ:rotate-z,scale:scale,scaleX:scale-x,scaleY:scale-y,translate:translate,translateX:translate-x/x,translateY:translate-y/y,translateZ:translate-z/z,accentColor:ac-c,caretColor:ca-c,scrollBehavior:scr-bhv,scrollbar:scr-bar,scrollbarColor:scr-bar-c,scrollbarGutter:scr-bar-g,scrollbarWidth:scr-bar-w,scrollMargin:scr-m,scrollMarginLeft:scr-ml,scrollMarginRight:scr-mr,scrollMarginTop:scr-mt,scrollMarginBottom:scr-mb,scrollMarginBlock:scr-my/scrollMarginY,scrollMarginBlockEnd:scr-mbe,scrollMarginBlockStart:scr-mbt,scrollMarginInline:scr-mx/scrollMarginX,scrollMarginInlineEnd:scr-me,scrollMarginInlineStart:scr-ms,scrollPadding:scr-p,scrollPaddingBlock:scr-py/scrollPaddingY,scrollPaddingBlockStart:scr-pbs,scrollPaddingBlockEnd:scr-pbe,scrollPaddingInline:scr-px/scrollPaddingX,scrollPaddingInlineEnd:scr-pe,scrollPaddingInlineStart:scr-ps,scrollPaddingLeft:scr-pl,scrollPaddingRight:scr-pr,scrollPaddingTop:scr-pt,scrollPaddingBottom:scr-pb,scrollSnapAlign:scr-sa,scrollSnapStop:scrs-s,scrollSnapType:scrs-t,scrollSnapStrictness:scrs-strt,scrollSnapMargin:scrs-m,scrollSnapMarginTop:scrs-mt,scrollSnapMarginBottom:scrs-mb,scrollSnapMarginLeft:scrs-ml,scrollSnapMarginRight:scrs-mr,scrollSnapCoordinate:scrs-c,scrollSnapDestination:scrs-d,scrollSnapPointsX:scrs-px,scrollSnapPointsY:scrs-py,scrollSnapTypeX:scrs-tx,scrollSnapTypeY:scrs-ty,scrollTimeline:scrtl,scrollTimelineAxis:scrtl-a,scrollTimelineName:scrtl-n,touchAction:tch-a,userSelect:us,overflow:ov,overflowWrap:ov-wrap,overflowX:ov-x,overflowY:ov-y,overflowAnchor:ov-a,overflowBlock:ov-b,overflowInline:ov-i,overflowClipBox:ovcp-bx,overflowClipMargin:ovcp-m,overscrollBehaviorBlock:ovs-bb,overscrollBehaviorInline:ovs-bi,fill:fill,stroke:stk,strokeWidth:stk-w,strokeDasharray:stk-dsh,strokeDashoffset:stk-do,strokeLinecap:stk-lc,strokeLinejoin:stk-lj,strokeMiterlimit:stk-ml,strokeOpacity:stk-op,srOnly:sr,debug:debug,appearance:ap,backfaceVisibility:bfv,clipPath:cp-path,hyphens:hy,mask:msk,maskImage:msk-i,maskSize:msk-s,textSizeAdjust:txt-adj,container:cq,containerName:cq-n,containerType:cq-t,cursor:cursor,textStyle:textStyle";
const classNameByProp = /* @__PURE__ */ new Map();
const shorthands = /* @__PURE__ */ new Map();
utilities.split(",").forEach((utility) => {
  const [prop, meta] = utility.split(":");
  const [className, ...shorthandList] = meta.split("/");
  classNameByProp.set(prop, className);
  if (shorthandList.length) {
    shorthandList.forEach((shorthand) => {
      shorthands.set(shorthand === "1" ? className : shorthand, prop);
    });
  }
});
const resolveShorthand = (prop) => shorthands.get(prop) || prop;
const context = {
  hash: true,
  conditions: {
    shift: sortConditions,
    finalize: finalizeConditions,
    breakpoints: { keys: ["base", "sm", "md", "tablet", "lg", "xl", "desktop", "2xl"] }
  },
  utility: {
    transform: (prop, value) => {
      const key = resolveShorthand(prop);
      const propKey = classNameByProp.get(key) || hypenateProperty(key);
      return { className: `${propKey}_${withoutSpace(value)}` };
    },
    hasShorthand: true,
    toHash: (path, hashFn) => hashFn(path.join(":")),
    resolveShorthand
  }
};
const cssFn = createCss(context);
const css = (...styles) => cssFn(mergeCss(...styles));
css.raw = (...styles) => mergeCss(...styles);
const { mergeCss, assignCss } = createMergeCss(context);
function cx() {
  let str = "", i = 0, arg;
  for (; i < arguments.length; ) {
    if ((arg = arguments[i++]) && typeof arg === "string") {
      str && (str += " ");
      str += arg;
    }
  }
  return str;
}
const { ContextProvider, useStore } = createState(
  "Logger",
  (set, get) => ({
    logs: [],
    historyLength: 100,
    // eslint-disable-next-line @typescript-eslint/require-await -- kept for eventual future integration with other services
    addLog: async (level, content) => {
      set((state) => {
        return {
          ...state,
          logs: [
            ...state.logs.slice(state.logs.length - state.historyLength + 1),
            { level, content, timestamp: /* @__PURE__ */ new Date() }
          ]
        };
      });
    },
    logger: {
      debug: (...content) => get().addLog("debug", content),
      info: (...content) => get().addLog("info", content),
      warn: (...content) => get().addLog("warn", content),
      error: (...content) => get().addLog("error", content)
    }
  })
);
function LoggerProvider({ children }) {
  return /* @__PURE__ */ jsx(ContextProvider, { children });
}
const useLogger = () => {
  return useStore((state) => state.logger);
};
const useLogs = () => {
  return useStore((state) => state.logs);
};
function LogString({ content }) {
  return /* @__PURE__ */ jsxs("div", { className: css({ color: "orange.700" }), children: [
    "'",
    content,
    "'"
  ] });
}
function LogNumber({ content }) {
  return /* @__PURE__ */ jsx("div", { className: css({ color: "blue.900" }), children: content });
}
function LogBigInt({ content }) {
  return /* @__PURE__ */ jsxs("div", { className: css({ color: "green.700" }), children: [
    content.toString(),
    "n"
  ] });
}
function LogBoolean({ content }) {
  return /* @__PURE__ */ jsx("div", { className: css({ color: "blue.900" }), children: JSON.stringify(content) });
}
function LogUndefined() {
  return /* @__PURE__ */ jsx("div", { className: css({ color: "gray.400" }), children: "undefined" });
}
function LogSymbol({ content }) {
  return /* @__PURE__ */ jsxs("div", { className: css({ color: "orange.700" }), children: [
    "Symbol(",
    content.description,
    ")"
  ] });
}
function LogFunction({ content }) {
  return /* @__PURE__ */ jsx("div", { className: css({ fontStyle: "italic" }), children: content.toString() });
}
function LogObject({
  content,
  LoggerRow: LoggerRow2
}) {
  const [minimize, toggleMinimize] = useToggle(true);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        name: "Espandi",
        onClick: toggleMinimize,
        type: "button",
        className: css({
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start"
        }),
        children: [
          /* @__PURE__ */ jsxs("div", { className: css({ fontWeight: "semibold", color: "gray.700" }), children: [
            minimize ? "+" : "-",
            " Object"
          ] }),
          minimize ? /* @__PURE__ */ jsx(
            "div",
            {
              className: css({
                wordBreak: "break-all",
                textAlign: "left",
                flex: "1",
                ml: "2",
                fontWeight: "light",
                color: "gray.400",
                overflow: "hidden",
                lineClamp: "1"
              }),
              children: JSON.stringify(content)
            }
          ) : null
        ]
      }
    ),
    !minimize && /* @__PURE__ */ jsx(
      "div",
      {
        className: css({ mt: "1", ml: "5", display: "grid" }),
        style: {
          gridTemplateColumns: "fit-content(0) minmax(0, 1fr)"
        },
        children: Object.entries(content).map(([key, element]) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: css({
                color: "gray.300",
                textAlign: "right",
                mt: "1",
                mb: "1",
                pr: "2",
                mr: "2",
                borderRightWidth: "1px",
                borderColor: "gray.200"
              }),
              children: key
            }
          ),
          /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(LoggerRow2, { content: element }) })
        ] }, key))
      }
    )
  ] });
}
function LogNull() {
  return /* @__PURE__ */ jsx("div", { className: css({ color: "gray.400" }), children: "null" });
}
function LogArray({
  content,
  LoggerRow: LoggerRow2
}) {
  const [minimize, toggleMinimize] = useToggle(true);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        name: "Espandi",
        onClick: toggleMinimize,
        type: "button",
        className: css({
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start"
        }),
        children: [
          /* @__PURE__ */ jsxs("div", { className: css({ fontWeight: "semibold", color: "gray.700" }), children: [
            minimize ? "+" : "-",
            " Array (",
            content.length,
            ")"
          ] }),
          minimize ? /* @__PURE__ */ jsx(
            "div",
            {
              className: css({
                wordBreak: "break-all",
                textAlign: "left",
                flex: "1",
                ml: "2",
                fontWeight: "light",
                color: "gray.400",
                overflow: "hidden",
                lineClamp: 1
              }),
              children: JSON.stringify(content)
            }
          ) : null
        ]
      }
    ),
    !minimize && /* @__PURE__ */ jsx(
      "div",
      {
        className: css({ mt: "1", ml: "5", display: "grid" }),
        style: {
          gridTemplateColumns: "fit-content(0) minmax(0, 1fr)"
        },
        children: content.map((element, index) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: css({
                color: "gray.300",
                textAlign: "right",
                mt: "1",
                mb: "1",
                pr: "2",
                mr: "2",
                borderRightWidth: "1px",
                borderColor: "gray.200"
              }),
              children: index
            }
          ),
          /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(LoggerRow2, { content: element }) })
        ] }, md5(JSON.stringify(element))))
      }
    )
  ] });
}
function LoggerContent({
  content,
  LoggerRow: PropLoggerRow
}) {
  if (content === null) {
    return /* @__PURE__ */ jsx(LogNull, {});
  }
  switch (typeof content) {
    case "string":
      return /* @__PURE__ */ jsx(LogString, { content });
    case "number":
      return /* @__PURE__ */ jsx(LogNumber, { content });
    case "bigint":
      return /* @__PURE__ */ jsx(LogBigInt, { content });
    case "boolean":
      return /* @__PURE__ */ jsx(LogBoolean, { content });
    case "undefined":
      return /* @__PURE__ */ jsx(LogUndefined, {});
    case "symbol":
      return /* @__PURE__ */ jsx(LogSymbol, { content });
    case "function":
      return /* @__PURE__ */ jsx(LogFunction, { content });
    case "object":
      if (Array.isArray(content)) {
        return /* @__PURE__ */ jsx(LogArray, { content, LoggerRow: PropLoggerRow });
      }
      return /* @__PURE__ */ jsx(LogObject, { content, LoggerRow: PropLoggerRow });
    default:
      return null;
  }
}
function LoggerRow({ content }) {
  return /* @__PURE__ */ jsx("div", { className: css({ fontSize: "sm", mt: "1", mb: "1" }), children: /* @__PURE__ */ jsx(LoggerContent, { content, LoggerRow }) });
}
const getLogLevelClassName = (level) => {
  switch (level) {
    case "debug":
      return { borderColor: "gray.300", color: "gray.400" };
    case "info":
      return { borderColor: "gray.500", color: "gray.600" };
    case "warn":
      return { borderColor: "yellow.400", color: "yellow.500" };
    case "error":
      return { borderColor: "orange.400", color: "orange.600" };
    default:
      return { borderColor: "gray.300", color: "gray.600" };
  }
};
const getLogLevelLabel = (level) => {
  switch (level) {
    case "debug":
      return "debug";
    case "info":
      return "info";
    case "warn":
      return "warn";
    case "error":
      return "error";
    default:
      return "debug";
  }
};
function LoggerHistory() {
  const logs = useLogs();
  const sortedLogs = useMemo(() => {
    return [...logs].sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );
  }, [logs]);
  const onCopyClick = useCallback((log) => {
    void navigator.clipboard.writeText(
      JSON.stringify(
        log.content,
        (_, value) => typeof value === "bigint" ? value.toString() : value,
        2
      )
    ).catch();
  }, []);
  const onCopyClicks = useMemo(() => {
    return sortedLogs.map((log) => () => {
      onCopyClick(log);
    });
  }, [onCopyClick, sortedLogs]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: css({ display: "grid" }),
      style: {
        gridTemplateColumns: "fit-content(0) minmax(0, 1fr)"
      },
      children: sortedLogs.map((log, index) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            name: "Copia",
            type: "button",
            onClick: onCopyClicks[index],
            className: cx(
              css(
                {
                  pos: "relative",
                  display: "flex",
                  flexDir: "column",
                  alignItems: "flex-end",
                  justifyContent: "flex-start",
                  my: "1",
                  py: "1",
                  px: "2",
                  textAlign: "right",
                  fontSize: "xs",
                  fontWeight: "bold",
                  borderRightWidth: "1px",
                  cursor: "pointer"
                },
                getLogLevelClassName(log.level)
              ),
              "group"
            ),
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: css({
                    _groupHover: { opacity: "0" },
                    opacity: "1",
                    transition: "opacity"
                  }),
                  children: /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: css({
                        transition: "all",
                        translate: "auto",
                        translateY: "0",
                        _groupHover: {
                          translateY: "1/2"
                        }
                      }),
                      children: getLogLevelLabel(log.level)
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: css({
                    pos: "absolute",
                    inset: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "opacity",
                    opacity: "0",
                    _groupHover: { opacity: "1" }
                  }),
                  children: /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: css({
                        transition: "all",
                        translate: "auto",
                        translateY: "-1/2",
                        _groupHover: {
                          translateY: "0"
                        }
                      }),
                      children: "copy"
                    }
                  )
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: cx(
              css({ ml: "2", mt: "1", mb: "1", pos: "relative" }),
              "group"
            ),
            children: [
              (Array.isArray(log.content) ? log.content : [log.content]).map(
                (row) => /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: css({
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-start"
                    }),
                    children: [
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          className: css(
                            { mr: "2", fontSize: "sm", mt: "1", mb: "1" },
                            getLogLevelClassName(log.level)
                          ),
                          children: "•"
                        }
                      ),
                      /* @__PURE__ */ jsx("div", { className: css({ fontFamily: "mono" }), children: /* @__PURE__ */ jsx(LoggerRow, { content: row }) })
                    ]
                  },
                  `${log.timestamp.getTime().toString()}_${md5(JSON.stringify(row))}`
                )
              ),
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: css({
                    _groupHover: { opacity: "0" },
                    opacity: "0.8",
                    transition: "opacity",
                    pos: "absolute",
                    top: "0",
                    right: "0",
                    overflow: "hidden"
                  }),
                  children: /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: css({
                        transition: "all",
                        translate: "auto",
                        translateX: "0",
                        _groupHover: {
                          translateX: "1/2"
                        },
                        bgColor: "gray.100",
                        color: "gray.500",
                        rounded: "md",
                        p: "1",
                        mr: "1",
                        fontSize: "xs",
                        lineHeight: "none"
                      }),
                      children: format(log.timestamp, "HH:mm:ss.SSS")
                    }
                  )
                }
              )
            ]
          }
        )
      ] }, `log_${log.timestamp.getTime().toString()}`))
    }
  );
}
function LoggerConsole() {
  const logs = useLogs();
  const logsContainerRef = useRef(null);
  const [keepLogsScrolled, setKeepLogsScrolled] = useState(true);
  useEffect(() => {
    const div = logsContainerRef.current;
    if (div && keepLogsScrolled) {
      div.scrollTo(0, div.scrollHeight);
    }
  }, [keepLogsScrolled, logs]);
  useEffect(() => {
    const div = logsContainerRef.current;
    if (div) {
      div.scrollTo(0, div.scrollHeight);
      const listener = () => {
        setKeepLogsScrolled(
          div.scrollTop + div.offsetHeight === div.scrollHeight
        );
      };
      div.addEventListener("scroll", listener);
      return () => {
        div.removeEventListener("scroll", listener);
      };
    }
  }, []);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: css({
        borderWidth: "1px",
        borderColor: "gray.200",
        rounded: "md",
        overflow: "hidden"
      }),
      children: /* @__PURE__ */ jsx(
        "div",
        {
          ref: logsContainerRef,
          className: css({
            maxH: "500px",
            overflowY: "auto",
            overflowX: "auto",
            py: "2"
          }),
          children: logs.length > 0 ? /* @__PURE__ */ jsx(LoggerHistory, {}) : /* @__PURE__ */ jsx(
            "div",
            {
              className: css({
                textAlign: "center",
                color: "gray.300",
                pt: "4",
                pb: "4"
              }),
              children: "No logs yet..."
            }
          )
        }
      )
    }
  );
}
export {
  LoggerConsole,
  LoggerProvider,
  useLogger,
  useLogs
};
